// 261015 P2b-1 Bước 8 — logic THUẦN của "Chấp nhận, có lý do" (C6), khối "Đã chấp nhận" và ô hiển thị neo ở bảng cao trào. KHÔNG import antd (luật jest của admin-presentation).
// BE là bên quyết (người = token, thời gian, dấu nội dung, ERROR không chấp nhận được); FE chỉ dựng lệnh + hiển thị.
import type { AcceptableGate, OutlineBeat, OutlinePatchOp, OutlineViolation, PatchResponse } from '../../../types/adaptation';
import { formatShortDateTime } from '../adaptationUtils';

/** Khớp BE `ACCEPTABLE_GATES` (`outlineAcceptance.ts`) — ba mã mà chính message lint nói "cố ý thì cứ để". */
export const ACCEPTABLE_GATES: readonly AcceptableGate[] = ['BEAT_PLANT_MISSING', 'BEAT_PLANT_SAME_CHAPTER', 'BEAT_GAP'];
export const MIN_ACCEPT_REASON = 10;
export const MAX_ACCEPT_REASON = 300;
/** Giới hạn nhập của ô lý do = giới hạn BE (bộ đếm `showCount` hiện "n / 300"). Ký tự trắng thừa bị gộp lúc gửi ⇒ chữ gửi đi luôn ≤ giá trị này. */
export const ACCEPT_REASON_INPUT_MAX = MAX_ACCEPT_REASON;
/** `ref` của BEAT_GAP: tính chất toàn cục của phân tập (`refs: []`), không phải lỗi của một cao trào. */
export const BEAT_GAP_REF = '*';

export const isAcceptableGate = (gate: unknown): gate is AcceptableGate => typeof gate === 'string' && (ACCEPTABLE_GATES as readonly string[]).includes(gate);

/** Dải lỗi: nút "Chấp nhận…" chỉ cho cảnh báo WARN thuộc 3 mã, còn là lỗi (`kind` vắng hoặc `defect`) — ERROR không bao giờ. */
export const canOfferAccept = (v: Pick<OutlineViolation, 'gate' | 'severity' | 'kind'>): boolean =>
  v.severity === 'WARN' && isAcceptableGate(v.gate) && (v.kind === undefined || v.kind === 'defect');

/** Các đích có thể chấp nhận của một hàng (hàng gộp nhiều ref ⇒ từng ref, Q3); BEAT_GAP ⇒ `['*']`. */
export const acceptTargets = (v: Pick<OutlineViolation, 'gate' | 'refs'>): string[] =>
  v.gate === 'BEAT_GAP' ? [BEAT_GAP_REF] : Array.from(new Set((v.refs ?? []).filter(Boolean)));

const squash = (s: unknown): string => String(s ?? '').replace(/\s+/g, ' ').trim();

export const acceptReasonState = (reason: string): { ok: boolean; length: number; hint: string } => {
  const length = squash(reason).length;
  const ok = length >= MIN_ACCEPT_REASON && length <= MAX_ACCEPT_REASON;
  return { ok, length, hint: ok ? '' : length < MIN_ACCEPT_REASON ? `Lý do cần ít nhất ${MIN_ACCEPT_REASON} ký tự (hiện ${length})` : `Lý do tối đa ${MAX_ACCEPT_REASON} ký tự` };
};

/** Nút "Chấp nhận" của hộp thoại bật khi có ≥ 1 đích được chọn và lý do hợp lệ. */
export const canSubmitAccept = (_gate: AcceptableGate, refs: readonly string[], reason: string): boolean => refs.length > 0 && acceptReasonState(reason).ok;

/** Q3: MỘT `acceptWarning` mỗi ref đã chọn, cùng một lý do; BE tự lấy người (không gửi `by`). BEAT_GAP luôn một lệnh `ref: '*'`. */
export const buildAcceptOps = (gate: AcceptableGate, refs: readonly string[], reason: string): OutlinePatchOp[] => {
  const text = squash(reason).slice(0, MAX_ACCEPT_REASON);
  const targets = gate === 'BEAT_GAP' ? (refs.length ? [BEAT_GAP_REF] : []) : Array.from(new Set(refs.filter((r) => r && r !== BEAT_GAP_REF)));
  return targets.map((ref) => ({ op: 'acceptWarning', gate, ref, reason: text }));
};

export const buildRevokeOp = (gate: AcceptableGate, ref: string): OutlinePatchOp => ({ op: 'revokeAcceptance', gate, ref: gate === 'BEAT_GAP' ? BEAT_GAP_REF : ref });

// ── khối "Đã chấp nhận" ──────────────────────────────────────────────────────────────────────

export interface AcceptedRow {
  key: string; gate: AcceptableGate; ref: string;
  reason: string; by: string; /** tên hiển thị: `byName` → `by` (bản ghi cũ) → '' */ byLabel: string; at: string; atText: string; via: string;
  revokeOp: OutlinePatchOp;
}

/** Mỗi mục `kind:'accepted'` của GET ⇒ (các) hàng của khối gập. Thiếu `acceptance` / mã ngoài 3 mã ⇒ bỏ (BE lạ không làm vỡ màn hình). */
export const acceptedRowsOf = (violations: ReadonlyArray<OutlineViolation> | null | undefined): AcceptedRow[] => {
  const rows: AcceptedRow[] = [];
  for (const v of Array.isArray(violations) ? violations : []) {
    if (v?.kind !== 'accepted' || !v.acceptance || !isAcceptableGate(v.gate)) continue;
    const refs = v.gate === 'BEAT_GAP' ? [BEAT_GAP_REF] : (v.refs ?? []);
    for (const ref of refs) {
      rows.push({
        key: `${v.gate}|${ref}`, gate: v.gate, ref, reason: v.acceptance.reason, by: v.acceptance.by, byLabel: squash(v.acceptance.byName) || squash(v.acceptance.by), at: v.acceptance.at,
        atText: formatShortDateTime(v.acceptance.at), via: v.acceptance.via ?? 'manual', revokeOp: buildRevokeOp(v.gate, ref),
      });
    }
  }
  return rows;
};

/** "Cao trào b19 — «lý do» — bởi admin1 lúc 15/10 08:30" (BEAT_GAP: không nêu cao trào). */
export const acceptedLine = (r: AcceptedRow, nameOf: (id: string) => string = (x) => x): string =>
  `${r.ref === BEAT_GAP_REF ? '' : `${nameOf(r.ref)} — `}«${r.reason}» — bởi ${r.byLabel || 'không rõ'}${r.atText ? ` lúc ${r.atText}` : ''}${r.via === 'assistant' ? ' (qua Trợ lý)' : ''}`;

// ── ô "Chi tiết gieo" của bảng cao trào ─────────────────────────────────────────────────────

const QUOTE_SHOWN = 80;

export interface BeatPlantCell {
  /** `anchored` có trích nguồn xác minh · `added` do chuyển thể thêm · `source` SOURCE chưa neo · `missing` cao trào lớn chưa có · `none` không hiện gì. */
  kind: 'anchored' | 'added' | 'source' | 'missing' | 'none';
  text: string;
  /** Chỉ `anchored`: «trích ≤ 80 ký tự…» và "chương N". */
  quoteText: string; chapterText: string;
  /** Chỉ `added`: huy hiệu. */
  badge: string;
  /** Tooltip đủ chữ (trích đầy đủ / lý do). */
  title: string;
}

export const beatPlantCell = (b: Pick<OutlineBeat, 'weight' | 'plant' | 'plantOrigin' | 'setupEpisode' | 'plantAnchor' | 'plantReason'>): BeatPlantCell => {
  const empty = { text: '', quoteText: '', chapterText: '', badge: '', title: '' };
  const plant = typeof b.plant === 'string' ? b.plant.trim() : '';
  if (!plant) return { ...empty, kind: b.weight === 'major' && b.plantOrigin !== undefined ? 'missing' : 'none' };
  const text = `${plant}${b.setupEpisode ? ` (tập ${b.setupEpisode})` : ''}`;
  if (b.plantAnchor && b.plantOrigin !== 'ADDED') {
    const q = squash(b.plantAnchor.quote);
    return { kind: 'anchored', text, quoteText: `«${q.length > QUOTE_SHOWN ? `${q.slice(0, QUOTE_SHOWN).trimEnd()}…` : q}»`, chapterText: `chương ${b.plantAnchor.orderIndex}`, badge: '', title: `${plant}\nTrích chương ${b.plantAnchor.orderIndex}: «${q}»` };
  }
  if (b.plantOrigin === 'ADDED') {
    return { kind: 'added', text, quoteText: '', chapterText: '', badge: 'do chuyển thể thêm', title: `${plant}\nDo chuyển thể thêm${b.plantReason ? ` — lý do: ${b.plantReason}` : ''}` };
  }
  return { kind: 'source', text, quoteText: '', chapterText: '', badge: '', title: plant };
};

// ── thông báo sau PATCH ─────────────────────────────────────────────────────────────────────

/** Câu bổ sung sau khi ghi: giữ "đã duyệt" (C5) hay kéo về nháp. Không ghi / truyện vốn là nháp ⇒ rỗng. */
export const describeKeptApproval = (statusBefore: string | undefined, r: Pick<PatchResponse, 'written' | 'keptApproval' | 'status'>): string => {
  if (!r.written || statusBefore !== 'APPROVED') return '';
  return r.keptApproval && r.status === 'APPROVED' ? 'Khung xương vẫn đã duyệt (chỉ sửa metadata có neo).' : 'Khung xương về bản nháp — cần duyệt lại.';
};

// ── 261015 P2b-3 F8: huy hiệu "từ vật liệu quét" ở bảng cao trào ─────────────────────────────

export interface BeatMaterialBadge { text: string; title: string }

/** Cao trào do `addBeatFromMaterial` thêm (có `materialRef`) ⇒ huy hiệu; cao trào cũ / `materialRef` hỏng ⇒ null. Tooltip nêu quyển, mục và (nếu có neo đoạn nổ) chương. */
export const beatMaterialBadge = (b: Pick<OutlineBeat, 'materialRef' | 'payoffAnchor'>): BeatMaterialBadge | null => {
  const m = b?.materialRef;
  if (!m || !Number.isInteger(m.volumeIndex) || !Number.isInteger(m.index)) return null;
  const ch = b.payoffAnchor && Number.isInteger(b.payoffAnchor.orderIndex) ? ` · đoạn nổ ở chương ${b.payoffAnchor.orderIndex}` : '';
  return { text: 'từ vật liệu quét', title: `Cao trào thêm từ vật liệu quét: quyển ${m.volumeIndex}, mục ${m.index}${ch}` };
};
